import React, { ReactElement, useCallback, useMemo, useState } from "react";
import { isNil, pluck } from "ramda";

import { Button, ButtonVariants, NavigateButton } from "affinityUI/elements/button";
import { Grid } from "affinityUI/elements/grid";
import { EmptyState } from "affinityUI/features/emptyState";
import { Heading } from "affinityUI/structures/heading";
import { ConfirmatoryTestInfoTag } from "components/app/components/confirmatoryTestInfoTag";
import { Restricted } from "lib/auth";
import type { PatientData } from "types/patient";

import { PatientDetails } from "./patientDetails";
import { ResultsTable } from "./resultsTable";

const WithDevice = () => (
  <>
    Patient has been issued a device, but has not tested yet.
    <br />
    Await patient&apos;s first test to see result history
  </>
);

const WithoutDevice = () => (
  <>
    Patient does not have a device and has no previous results. <br /> Request a device to get a patient started with
    Liberty
  </>
);

const getResultIds = pluck("id");

export const Patient: React.FC<{
  patient: PatientData;
  savePdf: (resultIds: string[]) => Promise<unknown>;
  normalRanges: Maybe<NormalRanges>;
  neutrophilThreshold?: Maybe<number>;
  detailsActions?: Maybe<ReactElement>;
}> = ({ patient, normalRanges, neutrophilThreshold, savePdf, detailsActions }) => {
  const { testResults, enrollmentDate, disEnrollmentDate } = patient;
  const hasDevice = !isNil(enrollmentDate) && isNil(disEnrollmentDate);
  const haemoglobinRange =
    patient.sex === "Female" ? normalRanges?.haemoglobinRangeFemale : normalRanges?.haemoglobinRangeMale;
  const patientNormalRanges = normalRanges ? { ...normalRanges, haemoglobinRange } : null;
  const checkBelowThreshold = useMemo(
    () => testResults?.some(({ belowNeutrophilThreshold }) => belowNeutrophilThreshold),
    [testResults]
  );

  const [isDownloadBusy, setIsDownloadBusy] = useState(false);

  const handleDownloadResults = useCallback(() => {
    setIsDownloadBusy(true);
    const ids = getResultIds(testResults ?? []);
    savePdf(ids).then(() => {
      setIsDownloadBusy(false);
    });
  }, [savePdf, testResults]);

  return (
    <Grid>
      <PatientDetails patient={patient} detailsActions={detailsActions} />
      <Grid $tight>
        <Heading>
          <Heading.SectionTitle>Result history</Heading.SectionTitle>
          {neutrophilThreshold && checkBelowThreshold ? (
            <Heading.Meta>
              <ConfirmatoryTestInfoTag neutrophilThreshold={neutrophilThreshold} />
            </Heading.Meta>
          ) : null}
          <Heading.Actions>
            <Restricted permits={["view_resultdata"]}>
              <Button disabled={isDownloadBusy} onClick={handleDownloadResults} variant={ButtonVariants.LINK}>
                Download Results
              </Button>
            </Restricted>{" "}
            <Restricted permits={["add_resultdata"]}>
              <NavigateButton to="result/new" variant={ButtonVariants.SECONDARY}>
                Add result
              </NavigateButton>
            </Restricted>
          </Heading.Actions>
        </Heading>
        <Restricted
          permits={["view_resultdata"]}
          fallback={
            <EmptyState>
              <EmptyState.Message>You do not have permission to view results</EmptyState.Message>
            </EmptyState>
          }
        >
          {testResults?.length ? (
            <ResultsTable
              neutrophilThreshold={neutrophilThreshold}
              patientNormalRanges={patientNormalRanges}
              results={testResults}
            />
          ) : (
            <EmptyState>
              <EmptyState.Message>{hasDevice ? <WithDevice /> : <WithoutDevice />}</EmptyState.Message>
            </EmptyState>
          )}
        </Restricted>
      </Grid>
    </Grid>
  );
};
